export interface QuizQuestion {
  question: string
  options: string[]
  correctIndex: number
}

export interface QuizResult {
  /** ISO timestamp set once every answer is filled in; null while in progress. */
  completedAt: string | null
  /** One entry per question; null for questions not yet answered. */
  answers: (number | null)[]
  /** Correct answer indices for the quiz. */
  correctIndices: number[]
}

export interface Story {
  id: string
  title: string
  subtitle: string
  /** Full unstripped model response (planning prose, fences, JSON markup). */
  raw: string
  /** Parsed Japanese story text — source of truth for rendering & eval. */
  text: string
  html: string
  /** English translation of `text`, paragraph-aligned with the Japanese.
   *  Null when the model didn't supply one. Omitted on list rows. */
  translation?: string | null
  /** Public URL of the generated MP3 narration. Null until synthesized. */
  audio?: string | null
  createdAt: string
  /** Null until the quiz has been generated. Lazily backfilled on first open. */
  quiz: QuizQuestion[] | null
  /** Null until the user picks at least one answer. Answers can be changed,
   *  and each change is persisted, so this stays in sync with the latest picks. */
  quizResult: QuizResult | null
  /** Library-row indicator. Mirrors `quizResult.completedAt` on the list endpoint. */
  quizCompletedAt?: string | null
  /** Library-row indicator: true when the quiz has been completed and
   *  every answer matched. False otherwise — including when not completed.
   *  Pair with `quizCompletedAt` to distinguish "failed" from "unanswered". */
  quizPassed?: boolean
  /** Textbook title the story was generated for. Null for legacy stories or
   *  when the user had no textbook progress at generation time. Present on
   *  both the list and detail endpoints. */
  textbookTitle?: string | null
  /** Chapter (lesson) number the story was generated for. Null likewise. */
  chapterNumber?: number | null
}

export type KnowledgeItemType = "word" | "kanji" | "rule"

export interface KnowledgeItem {
  id: string
  type: KnowledgeItemType
  text: string
  reading: string | null
  meaning: string | null
  createdAt: string
}

/** Admin-only generation telemetry for a story, served by
 *  GET /v1/admin/stories/:id/generation. `*CostUsd` are null when the model
 *  isn't priced; the quiz fields are null until the quiz has been generated. */
export interface StoryGeneration {
  model: string
  prompt: string
  /** Raw, untrusted custom instruction the reader typed for this generation.
   *  Null when none was supplied. Render as escaped text only — never HTML. */
  customInstruction: string | null
  /** Snapshot of the knowledge items the story was generated from. */
  knowledgeItems: { id: string; type: string; text: string }[]
  inputTokens: number | null
  outputTokens: number | null
  storyCostUsd: number | null
  quizModel: string | null
  quizInputTokens: number | null
  quizOutputTokens: number | null
  quizCostUsd: number | null
  createdAt: string
}

/** Max length for user-supplied customInstruction. Enforced
 *  server-side; the web UI uses it to render a character counter and disable
 *  submission when exceeded. */
export const MAX_CUSTOM_INSTRUCTION_LENGTH = 300

/** Selectable story length, in paragraphs. The reader picks a value in this
 *  range; the API validates against it and the slider is bounded by it. */
export const MIN_STORY_PARAGRAPHS = 4
export const MAX_STORY_PARAGRAPHS = 12

/** Paragraph count to preselect for a reader who has covered `chapters`
 *  chapters. Mirrors the API's default length heuristic (grows with coverage,
 *  historically capped at 8) but never drops below the slider minimum. */
export const defaultStoryParagraphs = (chapters: number): number =>
  Math.max(MIN_STORY_PARAGRAPHS, Math.min(8, 3 + Math.floor(chapters / 2)))

export type UserFlagName = "isAdmin"

export interface UserFlags {
  isAdmin: boolean | null
}

export interface StoriesPostBody {
  customInstruction?: string
  length?: number
}
